import { useEffect, useState, useRef, useMemo } from "react";

// Highchart
import Highcharts from "highcharts";
import HC_more from "highcharts/highcharts-more";

import "assets/styles/components/highcharts.css";

// Hooks
import useChartSlice from "store/slice/charts/useChartSlice";
import useChartResize from "../hooks/useChartResize";
import useChartRender from "../hooks/useChartRender";
import useTooltipSeriesSpiderChart from "../hooks/useTooltipSeriesSpiderChart";
import useCustomiseLabelsSpiderwebs from "../hooks/useCustomiseLabelsSpiderwebs";

// Config
import { getOptions } from "./DomainConfig";

// Types
import { SpiderWebChart } from "store/slice/charts";
import { IBaseChartProps, IRefObjectForHighchartsReact } from "components/Charts/chart.types";

HC_more(Highcharts);

const useDomain = ({
    width,
    height,
    getData,
    hideRefSeries,
}: IBaseChartProps): {
    chartOptions: Highcharts.Options;
    chartData: SpiderWebChart;
    chartRef: React.MutableRefObject<IRefObjectForHighchartsReact | null>;
    containerRef: React.RefObject<HTMLDivElement> | null;
    showRoles: boolean;
    showNoDataModal: boolean;
    setShowRoles: React.Dispatch<React.SetStateAction<boolean>>;
    onCloseNoDataModal: () => void;
    showNames: boolean;
    setShowNames: React.Dispatch<React.SetStateAction<boolean>>;
} => {
    const rawData = useChartSlice(getData) as SpiderWebChart;
    const chartRef = useRef<IRefObjectForHighchartsReact>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [showNoDataModal, setShowNoDataModal] = useState<boolean>(false);

    const chartData = useMemo(() => {
        return {
            ...rawData,
            series: rawData.series.map((serie) =>
                serie.name?.includes("UQ") ? { ...serie, visible: !hideRefSeries } : serie
            ),
        };
    }, [hideRefSeries, rawData]);

    const hasNoPersonData = chartData.series.every((series: any) =>
        series.data.every(
            (point: any) =>
                point?.custom?.entity_type === "person" &&
                (point?.a === 0 || point?.a === null || point?.a === undefined) &&
                (point?.x === 0 || point?.x === null || point?.x === undefined) &&
                (point?.y === 0 || point?.y === null || point?.y === undefined) &&
                (point?.z === 0 || point?.z === null || point?.z === undefined)
        )
    );

    useEffect(() => {
        if (hasNoPersonData) {
            setShowNoDataModal(true);
        }
    }, [hasNoPersonData]);

    const onCloseNoDataModal = () => {
        setShowNoDataModal(false);
    };

    const legendSelected = useRef<number[]>(
        chartData.series.reduce((acc, serie, index) => {
            return serie.visible || !serie.name ? [...acc, index] : acc;
        }, [] as number[])
    );
    const [showRoles, setShowRoles] = useState(true);
    const [showNames, setShowNames] = useState(false);

    const [chartOptions, setChartOptions] = useState(getOptions(chartData.series, legendSelected.current));

    const { chartExtraOptions, resizeReRender, onChartResize } = useChartResize(width, height, "spiderweb");
    useChartRender(width, height, chartRef, [onChartResize]);
    useTooltipSeriesSpiderChart(setChartOptions, showRoles, showNames, legendSelected);
    useCustomiseLabelsSpiderwebs(showRoles, showNames, setChartOptions, legendSelected.current);

    useEffect(() => {
        if (chartData) {
            setChartOptions(({ chart, credits, legend, xAxis, ...currentOptions }) => ({
                ...currentOptions,
                chart: {
                    ...chart,
                    ...chartExtraOptions.current?.chart,
                },
                credits: {
                    ...credits,
                    position: {
                        ...credits?.position,
                        ...chartExtraOptions.current?.credits?.position,
                    },
                },
                legend: {
                    ...legend,
                    ...chartExtraOptions.current?.legend,
                },
                pane: {
                    ...chartExtraOptions.current?.pane,
                },
                xAxis: { ...xAxis, ...chartData?.xAxis },
            }));
        }
    }, [chartData, chartExtraOptions, resizeReRender, width, height]);

    return {
        chartOptions,
        chartData,
        chartRef,
        containerRef,
        showNames,
        showRoles,
        showNoDataModal,
        setShowRoles,
        setShowNames,
        onCloseNoDataModal,
    };
};

export default useDomain;
